import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, FileText, RefreshCw, Save, Send, CalendarClock, AlertOctagon, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, uid } from "@/lib/api";
import { PageHeader, Field, Select, StatusChip, Modal, displayName, fmtDate } from "@/components/common";
import { SenderSetupCard, RETURN_KEY } from "@/components/SenderSetup";

const PURPOSES = ["reply to their response", "introduction", "exhibition follow-up", "catalogue request", "quotation request", "sample request", "distribution enquiry", "partnership enquiry"];
export const LANGS = ["English", "Chinese (Simplified)", "Arabic", "Spanish", "French", "German", "Turkish", "Malay", "Indonesian"];
export const TONES = ["professional", "friendly", "formal", "concise"];

function Options({ o, setO, senders, templates, files }) {
  const set = (k, v) => setO({ ...o, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
        {[["ai", "AI personalised", Sparkles], ["template", "Template only", FileText]].map(([m, l, I]) => (
          <button key={m} data-testid={`compose-mode-${m}`} onClick={() => set("mode", m)} className={`flex h-11 items-center justify-center gap-2 rounded-lg text-sm font-semibold ${o.mode === m ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"}`}><I className="h-4 w-4" />{l}</button>
        ))}
      </div>
      <Field label="Sender business"><Select testid="compose-sender-select" value={o.sender_id} onChange={(v) => set("sender_id", v)} placeholder="Choose…" options={senders.map((s) => [s.id, `${s.business_name}${s.connection_status !== "active" ? " (not connected)" : ""}`])} /></Field>
      <Field label={o.mode === "template" ? "Template" : "Template as guidance (optional)"}><Select testid="compose-template-select" value={o.template_id} onChange={(v) => set("template_id", v)} placeholder={o.mode === "template" ? "Choose…" : "None"} options={templates.map((t) => [t.id, t.name])} /></Field>
      {o.mode === "ai" && <>
        <Field label="Purpose"><Select testid="compose-purpose-select" value={o.purpose} onChange={(v) => set("purpose", v)} options={PURPOSES} /></Field>
        <Field label="Language"><Select testid="compose-language-select" value={o.language} onChange={(v) => set("language", v)} options={LANGS} /></Field>
        <Field label="Tone"><Select testid="compose-tone-select" value={o.tone} onChange={(v) => set("tone", v)} options={TONES} /></Field>
        <Field label="Length"><Select testid="compose-length-select" value={o.length} onChange={(v) => set("length", v)} options={[["short", "Short"], ["medium", "Medium"], ["long", "Long"]]} /></Field>
        <div className="sm:col-span-2"><Field label="Relevant notes for this email" hint="Only these notes, the contact's facts and the sender profile are used. Saved contact notes are included automatically.">
          <textarea data-testid="compose-notes-textarea" rows={3} className="cd-textarea" value={o.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. We discussed private-label packaging at booth 12.1 B05" /></Field></div>
      </>}
      <Field label="Optional attachment (sent as a secure download link)"><Select testid="compose-attachment-select" value={o.attachment_file_id} onChange={(v) => set("attachment_file_id", v)} placeholder="None" options={files.map((f) => [f.id, f.filename])} /></Field>
    </div>
  );
}

function SendDialog({ m, onClose, onSent }) {
  const key = useRef(uid());
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/messages/${m.id}/send`, { idempotency_key: key.current, confirm: true, scheduled_at: when ? new Date(when).toISOString() : null });
      toast.success(when ? "Email scheduled" : "Email approved and queued for sending");
      onSent();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Modal open onClose={onClose} title="Approve and send" testid="send-confirm-dialog">
      <div className="space-y-3 text-sm">
        <div className="rounded-xl bg-slate-50 p-3 space-y-1">
          <p><b>To:</b> {m.to_name} &lt;{m.to_email}&gt;</p>
          <p><b>Sender business:</b> {m.sender?.business_name}</p>
          <p><b>From:</b> {m.from_display}</p>
          <p><b>Reply-to:</b> {m.sender?.reply_to}</p>
          <p><b>Subject:</b> {m.subject}</p>
          {m.attachments?.length > 0 && <p><b>Attachment link:</b> {m.attachments.map((a) => a.filename).join(", ")}</p>}
        </div>
        <Field label="Schedule for later (optional)"><input data-testid="send-schedule-input" type="datetime-local" className="cd-input" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <button data-testid="send-confirm-btn" disabled={busy} onClick={send} className="cd-btn-primary w-full">{busy ? "Sending…" : when ? "Confirm schedule" : "Confirm — send 1 email"}</button>
      </div>
    </Modal>
  );
}

export default function Composer() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [contact, setContact] = useState(null);
  const [senders, setSenders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [o, setO] = useState({ mode: params.get("mode") || "ai", purpose: params.get("purpose") || "introduction", language: "English", tone: "professional", length: "medium", notes: params.get("notes") || "" });
  const [m, setM] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [later, setLater] = useState(false);

  const loadMsg = (mid) => api.get(`/messages/${mid}`).then((r) => { setM(r.data); setDraft({ subject: r.data.subject, body: r.data.body }); return r.data; });

  useEffect(() => {
    api.get("/senders").then((r) => {
      setSenders(r.data);
      const act = r.data.filter((s) => s.connection_status === "active");
      const pick = r.data.find((s) => s.is_default)?.id || (act.length === 1 ? act[0].id : r.data.length === 1 ? r.data[0].id : "");
      setO((p) => ({ ...p, sender_id: p.sender_id || pick }));
      setLoaded(true);
    });
    api.get("/templates").then((r) => setTemplates(r.data.items));
    const mid = params.get("message");
    const cid = params.get("contact");
    if (mid) loadMsg(mid).then((x) => api.get(`/contacts/${x.contact_id}`).then((r) => setContact(r.data)));
    else if (cid) api.get(`/contacts/${cid}`).then((r) => { setContact(r.data); setO((p) => ({ ...p, sender_id: r.data.sender_id || p.sender_id || "" })); });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!m || m.gen_status === "done") return;
    const t = setInterval(() => loadMsg(m.id), 2000);
    return () => clearInterval(t);
  }, [m?.id, m?.gen_status]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/messages/generate", { contact_id: contact.id, ...o, template_id: o.template_id || null, attachment_file_id: o.attachment_file_id || null });
      setM(data);
      setDraft({ subject: data.subject, body: data.body });
      nav(`/compose?message=${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const saveDraft = async (quiet) => {
    try { const { data } = await api.patch(`/messages/${m.id}`, draft); setM(data); if (!quiet) toast.success("Draft saved"); return data; } catch (e) { toast.error(errMsg(e)); }
  };
  const regenerate = async () => { await api.post(`/messages/${m.id}/regenerate`); setM({ ...m, gen_status: "queued" }); };
  const openSend = async () => { const d = await saveDraft(true); if (d && !d.blockers.length) setSending(true); };
  const finishSetup = async () => {
    const d = await saveDraft(true);
    if (!d) return;
    sessionStorage.setItem(RETURN_KEY, `/compose?message=${m.id}`);
    nav("/senders");
  };
  const onCreated = (s) => { setSenders((x) => [...x, s]); setO((p) => ({ ...p, sender_id: s.id })); };
  const cancel = async () => { try { await api.post(`/messages/${m.id}/cancel`); loadMsg(m.id); } catch (e) { toast.error(errMsg(e)); } };

  if (!contact) return <p className="text-slate-500">Loading…</p>;
  const files = (contact.files || []).filter((f) => f.kind === "attachment");
  const isDraft = m?.status === "draft";
  const dirty = m && (draft.subject !== m.subject || draft.body !== m.body);

  return (
    <div className="max-w-3xl">
      <PageHeader title="Email composer" subtitle={<>To <Link className="font-semibold text-teal-700" to={`/contacts/${contact.id}`}>{displayName(contact)}</Link> · {contact.emails?.[0]?.value || "no email address"}</>} />
      {!m && (
        <div className="space-y-4">
        {loaded && senders.length === 0 && !later && <SenderSetupCard testid="compose-sender-setup" onCreated={onCreated} onLater={() => setLater(true)} />}
        {loaded && senders.length === 0 && later && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="compose-no-sender-note">Emails need a sender business. You can keep working on contacts and add one later.</p>}
        <div className="cd-card space-y-4">
          <Options o={o} setO={setO} senders={senders} templates={templates} files={files} />
          <p className="text-xs text-slate-500">{o.mode === "template" ? "Template only: approved variables are substituted; the message is not rewritten." : "AI uses only the selected business profile, the contact's facts and your notes. It will not invent meetings, prices or claims."}</p>
          <button data-testid="compose-generate-btn" disabled={busy || !o.sender_id || (o.mode === "template" && !o.template_id)} onClick={generate} className="cd-btn-primary w-full">
            {busy ? "Generating…" : o.mode === "ai" ? <><Sparkles className="h-4 w-4" />Generate AI email</> : <><FileText className="h-4 w-4" />Fill template</>}
          </button>
        </div>
        </div>
      )}
      {m && (
        <div className="space-y-4">
          <div className="cd-card space-y-2 text-sm" data-testid="compose-envelope">
            <div className="flex items-center justify-between"><p className="flex items-center gap-2 font-bold"><Building2 className="h-4 w-4 text-teal-700" />{m.sender?.business_name}</p><StatusChip status={m.status} testid="compose-message-status" /></div>
            <p><span className="text-slate-500">From:</span> <span data-testid="compose-from">{m.from_display}</span></p>
            <p><span className="text-slate-500">Reply-to:</span> {m.sender?.reply_to || <span className="text-rose-600">not set</span>}</p>
            <p><span className="text-slate-500">To:</span> {m.to_name} &lt;{m.to_email || "missing"}&gt;</p>
            <p><span className="text-slate-500">Mode:</span> {m.mode === "ai" ? "AI personalised" : "Template only"}{m.edited ? " · edited by you" : ""}</p>
            {m.attachments?.length > 0 && <p><span className="text-slate-500">Attachment link:</span> {m.attachments.map((a) => a.filename).join(", ")}</p>}
          </div>
          {m.gen_status !== "done" ? <div className="cd-card animate-pulse text-sm text-slate-500">Generating draft…</div> : (
            <div className="cd-card space-y-3">
              <Field label="Subject"><input data-testid="compose-subject-input" disabled={!isDraft} className="cd-input" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} /></Field>
              <Field label="Body"><textarea data-testid="compose-body-textarea" disabled={!isDraft} rows={14} className="cd-textarea font-[inherit]" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} /></Field>
              <p className="text-xs text-slate-400">A small footer with an unsubscribe link is added automatically.</p>
            </div>
          )}
          {m.blockers?.length > 0 && isDraft && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="compose-blockers">
              <p className="mb-1 flex items-center gap-2 font-bold text-rose-800"><AlertOctagon className="h-4 w-4" />Sending is blocked</p>
              <ul className="list-disc pl-5 text-sm text-rose-700">{m.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              {m.blockers.some((b) => b.includes("sending connection") || b.includes("not verified") || b.includes("archived") || b.includes("No sender")) && <button data-testid="compose-finish-setup-btn" onClick={finishSetup} className="mt-2 inline-block text-sm font-semibold text-teal-700">Save draft and finish sender setup →</button>}
              {dirty && <p className="mt-2 text-xs text-rose-600">Save the draft to re-check.</p>}
            </div>
          )}
          {m.error && !isDraft && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="compose-error">{m.error}</p>}
          {m.events?.length > 0 && <div className="cd-card text-xs text-slate-500">{m.events.map((e, i) => <p key={i}>{fmtDate(e.at)} — {e.detail}</p>)}</div>}
          {isDraft && m.gen_status === "done" && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="compose-send-btn" onClick={openSend} className="cd-btn-primary"><Send className="h-4 w-4" />Review & send</button>
              <button data-testid="compose-save-draft-btn" onClick={() => saveDraft(false)} className="cd-btn-secondary"><Save className="h-4 w-4" />Save draft</button>
              <button data-testid="compose-regenerate-btn" onClick={regenerate} className="cd-btn-secondary"><RefreshCw className="h-4 w-4" />Regenerate</button>
              <button data-testid="compose-new-btn" onClick={() => { setM(null); nav(`/compose?contact=${contact.id}`, { replace: true }); }} className="cd-btn-ghost">Start over</button>
            </div>
          )}
          {["pending", "scheduled"].includes(m.status) && <button data-testid="compose-cancel-btn" onClick={cancel} className="cd-btn-danger"><CalendarClock className="h-4 w-4" />Cancel unsent email</button>}
          {["pending", "scheduled", "sending"].includes(m.status) && <button onClick={() => loadMsg(m.id)} className="cd-btn-ghost">Refresh status</button>}
        </div>
      )}
      {sending && <SendDialog m={m} onClose={() => setSending(false)} onSent={() => { setSending(false); loadMsg(m.id); }} />}
    </div>
  );
}
